import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Save, ArrowLeft, Share2, X } from 'lucide-react';

interface DemoFormData {
  title: string;
  description: string;
  demo_url: string;
  thumbnail_url: string;
  tags: string[];
  status: 'active' | 'draft' | 'archived';
}

const DemoForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isEditing = id && id !== 'new';

  const [formData, setFormData] = useState<DemoFormData>({
    title: '',
    description: '',
    demo_url: '',
    thumbnail_url: '',
    tags: [],
    status: 'active'
  });
  
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState<'view' | 'edit'>('view');
  const [shares, setShares] = useState<any[]>([]);

  useEffect(() => {
    if (isEditing) {
      fetchDemo();
    }
  }, [id, isEditing]);

  const fetchDemo = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/demos/${id}?userEmail=${encodeURIComponent(user?.email || '')}`);
      const data = await response.json();
      
      if (data.success) {
        const demo = data.demo;
        setFormData({
          title: demo.title,
          description: demo.description || '',
          demo_url: demo.demo_url || '',
          thumbnail_url: demo.thumbnail_url || '',
          tags: demo.tags || [],
          status: demo.status
        });
        setShares(demo.shares || []);
      } else {
        setError(data.error || 'Failed to load demo');
      }
    } catch (err) {
      setError('Failed to load demo');
      console.error('Error fetching demo:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const url = isEditing ? `/api/demos/${id}` : '/api/demos';
      const method = isEditing ? 'PUT' : 'POST';
      
      const payload = {
        ...formData,
        created_by_user_id: user?.id,
        created_by_email: user?.email,
        userEmail: user?.email
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        navigate('/');
      } else {
        setError(data.error || 'Failed to save demo');
      }
    } catch (err) {
      setError('Failed to save demo');
      console.error('Error saving demo:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, newTag.trim()] });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
  };

  const handleShare = async () => {
    if (!shareEmail.trim()) {
      alert('Please enter an email address');
      return;
    }

    if (!shareEmail.endsWith('@salesforce.com')) {
      alert('Only @salesforce.com email addresses are allowed');
      return;
    }

    try {
      const response = await fetch(`/api/demos/${id}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shared_with_email: shareEmail,
          permission: sharePermission,
          shared_by_email: user?.email
        }),
      });

      const data = await response.json();

      if (data.success) {
        setShareEmail('');
        fetchDemo(); // Refresh to get updated shares
      } else {
        alert(data.error || 'Failed to share demo');
      }
    } catch (err) {
      alert('Failed to share demo');
      console.error('Error sharing demo:', err);
    }
  };

  const handleRemoveShare = async (shareId: string) => {
    if (!confirm('Remove access for this user?')) return;

    try {
      const response = await fetch(`/api/demos/${id}/share/${shareId}?userEmail=${encodeURIComponent(user?.email || '')}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        fetchDemo(); // Refresh to get updated shares
      } else {
        alert(data.error || 'Failed to remove share');
      }
    } catch (err) {
      alert('Failed to remove share');
      console.error('Error removing share:', err);
    }
  };

  if (loading && isEditing) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-gray-500">Loading demo...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Edit Demo' : 'Create New Demo'}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Title *
          </label>
          <input
            type="text"
            id="title"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Enter demo title"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Describe your demo"
          />
        </div>

        {/* Demo URL */}
        <div>
          <label htmlFor="demo_url" className="block text-sm font-medium text-gray-700 mb-1">
            Demo URL
          </label>
          <input
            type="url"
            id="demo_url"
            value={formData.demo_url}
            onChange={(e) => setFormData({ ...formData, demo_url: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="https://..."
          />
        </div>

        {/* Thumbnail URL */}
        <div>
          <label htmlFor="thumbnail_url" className="block text-sm font-medium text-gray-700 mb-1">
            Thumbnail URL
          </label>
          <input
            type="url"
            id="thumbnail_url"
            value={formData.thumbnail_url}
            onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="https://..."
          />
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tags
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Add a tag"
            />
            <button
              type="button"
              onClick={handleAddTag}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="text-purple-500 hover:text-purple-700"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            id="status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {/* Share Section (only when editing) */}
        {isEditing && (
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share Demo
            </h3>
            
            <div className="flex gap-2 mb-4">
              <input
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="user@salesforce.com"
              />
              <select
                value={sharePermission}
                onChange={(e) => setSharePermission(e.target.value as 'view' | 'edit')}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="view">View Only</option>
                <option value="edit">Can Edit</option>
              </select>
              <button
                type="button"
                onClick={handleShare}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Share
              </button>
            </div>

            {shares.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Shared with:</h4>
                {shares.map((share) => (
                  <div key={share.shared_with_email} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{share.shared_with_email}</div>
                      <div className="text-xs text-gray-500">
                        {share.permission === 'edit' ? 'Can edit' : 'View only'} • Shared {new Date(share.shared_at).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveShare(share.id || share.shared_with_email)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : isEditing ? 'Update Demo' : 'Create Demo'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default DemoForm;
